/** In-app notifications delivered by the server (spec section 13). */
import { z } from 'zod';
import { NotificationIdSchema } from '../ids.js';
import { CountSchema, CursorQuerySchema, IsoDateTimeSchema, QueryBooleanSchema, defineRoute, pageOf } from './common.js';
import { NotificationCategorySchema, NotificationLevelSchema, NotificationSchema } from './entities.js';

export const NotificationListQuerySchema = CursorQuerySchema.extend({
  unreadOnly: z.coerce.boolean().default(false),
  level: NotificationLevelSchema.optional(),
  category: NotificationCategorySchema.optional(),
  // notifications change: incremental polling. Only notifications created, read, acknowledged or
  // expired after this instant are returned (the server compares against its updated_at).
  updatedAfter: IsoDateTimeSchema.optional(),
  // notifications change: `false` returns unread only (query-string safe: `?includeRead=false`).
  includeRead: QueryBooleanSchema.default(true),
});
export type NotificationListQuery = z.infer<typeof NotificationListQuerySchema>;

export const NotificationPageSchema = pageOf(NotificationSchema).extend({
  unreadCount: CountSchema,
  // notifications change: unread notices with `required: true` (cannot be muted or dismissed).
  requiredUnread: CountSchema,
});
export type NotificationPage = z.infer<typeof NotificationPageSchema>;

export const NotificationAckRequestSchema = z
  .object({ ids: z.array(NotificationIdSchema).min(1).max(200), markRead: z.boolean().default(true) })
  .strict();

export const NotificationAckResponseSchema = z.object({ acknowledged: z.array(NotificationIdSchema), unreadCount: CountSchema });

export const notificationRoutes = {
  'notifications.list': defineRoute({
    id: 'notifications.list',
    method: 'GET',
    path: '/v1/notifications',
    auth: 'desktop',
    requestSchema: NotificationListQuerySchema,
    responseSchema: NotificationPageSchema,
    description: "Caller's notifications, newest first.",
  }),
  'notifications.ack': defineRoute({
    id: 'notifications.ack',
    method: 'POST',
    path: '/v1/notifications/ack',
    auth: 'desktop',
    requestSchema: NotificationAckRequestSchema,
    responseSchema: NotificationAckResponseSchema,
    description: 'Acknowledge (and optionally mark read) notifications.',
  }),
} as const;
